<template>
    <div class="d-flex flex-column h-100">
        <div class="d-flex align-items-center justify-content-end fs-13 flex-shrink-0 mb-2">
            <template v-for="(option, index) in views" :key="option.view">
                <span v-if="index > 0" class="text-muted">|</span>
                <a
                    href="javascript:"
                    class="period-option px-1"
                    :class="{active: option.view === view}"
                    @click="switchTo(option.view)"
                >{{ option.label }}</a>
            </template>
        </div>
        <div v-if="isEmpty" class="flex-fill d-flex align-items-center justify-content-center text-muted fs-13 text-center px-3">
            <span>
                {{ emptyMessage }}
                <a v-if="fillInUrl" :href="fillInUrl" class="fw-semibold">Doplnit údaje</a>
            </span>
        </div>
        <div v-show="!isEmpty" class="flex-fill" :style="{minHeight: height + 'px'}">
            <canvas ref="canvas"></canvas>
        </div>
    </div>
</template>
<script lang="ts">
import InsightsChart, {InsightsChartData, InsightsView} from '@/components/charts/InsightsChart';
import {computed, defineComponent, nextTick, onMounted, PropType, ref} from 'vue';

const LABELS: Record<InsightsView, string> = {
    moves: 'Poslední přecenění',
    appreciations: 'Od nákupu',
    mortgages: 'Hypotéky',
};

const EMPTY_MESSAGES: Record<InsightsView, string> = {
    moves: 'Změny se ukážou po prvním přecenění.',
    appreciations: 'Zhodnocení od nákupu uvidíte po vyplnění pořizovací ceny.',
    mortgages: 'Podíl vlastního jmění a dluhu uvidíte po vyplnění hypotéky.',
};

export default defineComponent({
    props: {
        chart: {
            type: Object as PropType<InsightsChartData>,
            required: true,
        },
        links: {
            type: Object as PropType<Record<string, string>>,
            required: true,
        },
        fillInUrl: {
            type: String,
            default: '',
        },
        language: {
            type: String,
            required: true,
        },
        currency: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        const canvas = ref<HTMLCanvasElement | null>(null);
        const view = ref<InsightsView>(props.chart.moves.length > 0 ? 'moves' : props.chart.appreciations.length > 0 ? 'appreciations' : 'moves');
        const height = ref(120);

        let insightsChart: InsightsChart | null = null;

        // the mortgage view is only offered when there is a mortgage to show
        const views = computed(() => (['moves', 'appreciations', 'mortgages'] as InsightsView[])
            .filter(option => option !== 'mortgages' || props.chart.mortgages.length > 0)
            .map(option => ({view: option, label: LABELS[option]})));

        const isEmpty = computed(() => props.chart[view.value].length === 0);
        const emptyMessage = computed(() => EMPTY_MESSAGES[view.value]);

        const switchTo = (next: InsightsView) => {
            view.value = next;

            nextTick(() => {
                if (insightsChart) {
                    insightsChart.switchTo(next);
                    height.value = insightsChart.preferredHeight();
                }
            });
        };

        onMounted(() => {
            insightsChart = new InsightsChart(
                canvas.value as HTMLCanvasElement,
                props.chart,
                props.links,
                props.language,
                props.currency,
                view.value,
            );
            height.value = insightsChart.preferredHeight();
        });

        return {canvas, view, views, height, isEmpty, emptyMessage, switchTo};
    },
});
</script>
